import {
  PULSE_BLOCK_STATE,
  PULSE_BLOCK_TYPE,
  type PulseBlock,
  type PulseBlockType,
} from '../types';

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

export const getLastNameComponent = (name: string): string =>
  name.split('/').filter(Boolean).at(-1) ?? name;

type DisplayNameResource =
  | string
  | {
      __typename?: string;
      displayName?: string | null;
      name?: string | null;
    }
  | null
  | undefined;

type PulseTitleResource = {
  displayName?: string | null;
  name?: string | null;
};

export const getDisplayName = (resource: DisplayNameResource): string => {
  if (!resource) return 'Unknown';
  if (typeof resource === 'string') return getLastNameComponent(resource);
  if (resource.__typename === 'Error') return 'Unknown';
  return (
    resource.displayName ||
    (resource.name ? getLastNameComponent(resource.name) : 'Unknown')
  );
};

export const getPulseTitle = (
  pulse?: PulseTitleResource,
  fallback?: string,
): string =>
  pulse?.displayName ||
  (pulse?.name ? getLastNameComponent(pulse.name) : fallback) ||
  'Pulse';

export const formatDate = (value?: string): string => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return dateFormatter.format(date);
};

const pulseBlockTypeLabels: Partial<Record<PulseBlockType, string>> = {
  [PULSE_BLOCK_TYPE.CHART]: 'Chart',
  [PULSE_BLOCK_TYPE.TABLE]: 'Table',
  [PULSE_BLOCK_TYPE.BIG_NUMBER]: 'Big number',
};

export const getPulseBlockTypeLabel = (
  blockType?: PulseBlockType | null,
): string =>
  blockType
    ? (pulseBlockTypeLabels[blockType] ?? 'Unknown block type')
    : 'Unknown';

export const isPulseBlockPending = (block: PulseBlock): boolean =>
  block.state === PULSE_BLOCK_STATE.PENDING;

export const isPulseBlockError = (block: PulseBlock): boolean =>
  block.state === PULSE_BLOCK_STATE.ERROR;

export const isPulseBlockSummaryPending = (block: PulseBlock): boolean =>
  isPulseBlockPending(block) && !block.summary;

export const isPulseBlockResultPending = (block: PulseBlock): boolean => {
  if (!isPulseBlockPending(block)) {
    return false;
  }

  const queryResult = block.currentResult?.queryResult;
  if (!queryResult?.resultData) {
    return true;
  }

  return (
    block.blockType === PULSE_BLOCK_TYPE.CHART && !queryResult.visualizationSpec
  );
};
